import CookieBanner from "./components/CookieBanner";
import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Sparkles,
  Cpu,
  Database,
  LineChart,
  BatteryCharging,
  Mail,
} from "lucide-react";
import "./App.css";

export default function App() {
  const [hasConsent, setHasConsent] = useState(() => {
    if (typeof window === "undefined") return false;
    return !!localStorage.getItem("cookieConsent");
  });

  const [visits, setVisits] = useState(null);

  useEffect(() => {
    // einfachen Fetch auf counter.php machen
    fetch("/counter.php")
      .then((res) => res.json())
      .then((data) => {
        if (typeof data.count === "number") {
          setVisits(data.count);
        }
      })
      .catch(() => {
        // wenn es fehlschlägt, Zähler einfach ignorieren
      });
  }, []);

  return (
    <div className="page">
      {/* Video-Overlay nur solange keine Cookie-Entscheidung getroffen wurde */}
      {!hasConsent && <IntroVideoOverlay />}

      {/* Background */}
      <div className="bg">
        <div className="bg-gradients" />
        <svg className="bg-grid" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern
              id="grid"
              width="40"
              height="40"
              patternUnits="userSpaceOnUse"
            >
              <path
                d="M 40 0 L 0 0 0 40"
                fill="none"
                stroke="white"
                strokeWidth="0.6"
              />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />
        </svg>
        <div className="bg-beam" />
      </div>

      {/* NAV */}
      <header className="nav">
        <div className="container nav-inner">
          <div className="brand">
            <LogoSVG className="logo" />
            <span className="brand-text">PlugIn-Gruppe.eu</span>
          </div>
          <nav className="nav-links">
            <a href="#ideen">Ideen</a>
            <a href="#innovationen">Innovationen</a>
            <a href="#investments">Investments</a>
            <a href="#kontakt">Kontakt</a>
          </nav>
          <a href="#kontakt" className="cta">
            <Mail size={16} />
            Anfragen
          </a>
        </div>
      </header>

      {/* HERO */}
      <section>
        <div className="container hero">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <p className="pill">
              <Sparkles size={14} />
              Kurz. Knackig. Zukunftsorientiert.
            </p>
            <h1 className="headline">
              Ideen, <span className="gradient">Innovationen</span> & Investments
              <br />
              für die Zukunft.
            </h1>
            <p className="lead">
              Die PlugIn-Gruppe steht für mutige Konzepte, schnelle Umsetzung
              und smarte Beteiligungen. Wir verbinden Wissen, Daten und KI –
              damit Visionen Wirklichkeit werden.
            </p>
            <div className="hero-buttons">
              <a href="#kontakt" className="btn-primary">
                Jetzt sprechen <ArrowRight size={16} />
              </a>
              <a href="#felder" className="btn-ghost">
                Was wir bauen
              </a>
            </div>
          </motion.div>

          {/* „Generierte“ Bilder als SVG */}
          <div className="cards">
            <Card title="Data City – Zukunftsskyline">
              <CitySVG />
            </Card>
            <Card title="Neural Mesh – KI-Sphäre">
              <NeuralSphereSVG />
            </Card>
            <Card title="Circuit Flow – Digitale Infrastruktur">
              <CircuitSVG />
            </Card>
          </div>
        </div>

        {/* Futuristisches Highlight: Marquee */}
        <div className="marquee-wrap">
          <div className="marquee-mask">
            <ul className="marquee">
              {Array.from({ length: 12 }).map((_, i) => (
                <li key={i} className="marquee-item">
                  <span className="dot" />
                  AI • Daten • Wissen → Zukunft
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* FELDER */}
      <section id="felder" className="container padY">
        <h2 className="h2">Unsere Schwerpunkte</h2>
        <p className="muted">
          Kurz & verspielt – mit Substanz. Wir investieren dort, wo Technologie
          echten Nutzen schafft.
        </p>
        <div className="focus-grid">
          <Focus
            icon={<Cpu size={18} />}
            title="Künstliche Intelligenz"
            text="Von Prototypen bis Produkt – Modelle, Agenten, Automatisierung."
          />
          <Focus
            icon={<Database size={18} />}
            title="Datenplattformen"
            text="Architektur, Pipelines, Governance – Daten als Produkt."
          />
          <Focus
            icon={<LineChart size={18} />}
            title="Industrial Analytics"
            text="Edge- & Cloud-Insights für Produktion & Logistik (Industrie 4.0)."
          />
          <Focus
            icon={<BatteryCharging size={18} />}
            title="Energie & Greentech"
            text="Digitale Optimierung, Speicher & Netze – effizient in die Zukunft."
          />
        </div>
      </section>

      {/* INNOVATIONEN */}
      <section id="innovationen" className="container padY">
        <div className="two-col">
          <div>
            <h3 className="h2">
              Von der Idee zum MVP – in Wochen, nicht Monaten
            </h3>
            <p className="muted">
              Gemeinsam validieren wir Hypothesen, shippen erste Versionen und
              skalieren, was funktioniert. Pragmatismus trifft Ambition.
            </p>
            <ul className="list">
              <li>
                <span className="list-dot" /> Design Sprints & technische Due
                Diligence
              </li>
              <li>
                <span className="list-dot" /> Daten- und KI-Werkbänke (MLOps,
                LLMOps)
              </li>
              <li>
                <span className="list-dot" /> Partnerschaften & Co-Investments
              </li>
            </ul>
          </div>
          <div className="demo-wrap">
            <div className="demo-glow" />
            <div className="demo-card">
              <div className="demo-head">
                <span className="muted small">
                  KI-Pilot: Prognose-Dashboard
                </span>
                <span className="muted xsmall">Live Demo</span>
              </div>
              <div className="demo-canvas">
                <DemoChartSVG />
              </div>
              <p className="muted small">
                Beispielvisualisierung: Datenströme, Anomalien, Vorhersagen –
                alles auf einen Blick.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* INVESTMENTS */}
      <section id="investments" className="container padY">
        <h3 className="h2">Investments mit Wirkung</h3>
        <p className="muted">
          Wir beteiligen uns an Teams, die Technologie als Hebel nutzen – fair,
          schnell und partnerschaftlich.
        </p>
        <div className="cards-3">
          <TagCard
            title="Pre-Seed & Seed"
            text="Ticketgrößen flexibel, Fokus auf Produkt-Markt-Fit."
          />
          <TagCard
            title="Smart Capital"
            text="Netzwerk, Go-to-Market & technische Exzellenz inklusive."
          />
          <TagCard
            title="Co-Creation"
            text="Gemeinsam bauen wir Produkte – nicht nur PowerPoint."
          />
        </div>
      </section>

      {/* KONTAKT */}
      <section id="kontakt" className="container padY">
        <div className="contact">
          <div>
            <h3 className="h2">Lass uns etwas PlugIn-würdiges bauen.</h3>
            <p className="muted">
              Schreib uns eine kurze Nachricht – wir melden uns schnell zurück.
            </p>
            <form className="form">
              <input required placeholder="Name" />
              <input required type="email" placeholder="E-Mail" />
              <textarea
                rows={4}
                placeholder="Kurz & knackig: Worum geht's?"
              />
              <button type="submit" className="btn-primary">
                Nachricht senden <ArrowRight size={16} />
              </button>
              <p className="muted xsmall">
                Mit dem Senden akzeptierst du unsere Datenschutzhinweise.
              </p>
            </form>
          </div>
          <div className="map-side">
            <div className="demo-glow" />
            <div className="map-frame">
              <MapDotsSVG />
            </div>
            <div className="muted small">
              <p>
                <span className="dim">E-Mail:</span> hello@plugin-gruppe.eu
              </p>
              <p>
                <span className="dim">Standort:</span> Europa – remote-first
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="footer">
  <div className="container footer-inner">
    <div className="brand">
      <LogoSVG className="logo small" />
      <span>© {new Date().getFullYear()} PlugIn-Gruppe.eu</span>
    </div>

    {visits !== null && (
      <div className="footer-counter">
        Besucher insgesamt: {visits.toLocaleString("de-DE")}
      </div>
    )}

    <div className="footer-links">
      <a href="/impressum.html">Impressum</a>
      <a href="/datenschutz.html">Datenschutz</a>
    </div>
  </div>
</footer>

      <style>{`
        @keyframes scroll { 0% { transform: translateX(0) } 100% { transform: translateX(-50%) } }
        @keyframes pulse { 0%,100% { opacity:.15 } 50% { opacity:.5 } }
      `}</style>

      {/* Cookie-Banner meldet zurück, wenn eine Entscheidung getroffen wurde */}
      <CookieBanner onConsentChange={() => setHasConsent(true)} />
    </div>
  );
}

/* ---------- kleine Helfer-Komponenten ---------- */
function Card({ title, children }) {
  return (
    <div className="card">
      <div className="card-grad" />
      <div className="card-title">{title}</div>
      <div className="card-media">{children}</div>
    </div>
  );
}

function Focus({ icon, title, text }) {
  return (
    <div className="focus">
      <div className="focus-top">
        <div className="focus-ico">{icon}</div>
        <div className="focus-title">{title}</div>
      </div>
      <p className="muted small">{text}</p>
    </div>
  );
}

function TagCard({ title, text }) {
  return (
    <div className="tag">
      <div className="tag-title">{title}</div>
      <p className="muted small">{text}</p>
    </div>
  );
}

/* ---------- SVGs (eingebettet, „generiert“) ---------- */
function CitySVG() {
  const buildings = Array.from({ length: 28 });
  const links = Array.from({ length: 24 });
  return (
    <svg
      viewBox="0 0 1200 750"
      xmlns="http://www.w3.org/2000/svg"
      className="svg"
    >
      <defs>
        <linearGradient id="g1" x1="0" x2="1">
          <stop offset="0%" stopColor="#67e8f9" stopOpacity=".9" />
          <stop offset="50%" stopColor="#e879f9" stopOpacity=".9" />
          <stop offset="100%" stopColor="#6ee7b7" stopOpacity=".9" />
        </linearGradient>
      </defs>
      <rect width="100%" height="100%" fill="#0a0a0a" />
      <ellipse
        cx="600"
        cy="680"
        rx="520"
        ry="120"
        fill="url(#g1)"
        opacity=".25"
      />
      {buildings.map((_, i) => {
        const x = 40 + i * 40;
        const h = 150 + Math.sin(i * 0.6) * 80 + (i % 3) * 40;
        return (
          <g key={i} opacity=".9">
            <rect
              x={x}
              y={600 - h}
              width="28"
              height={h}
              fill="none"
              stroke="url(#g1)"
              strokeWidth="2"
            />
            <path
              d={`M${x} ${600 - h} L${x + 28} ${600 - h}`}
              stroke="url(#g1)"
              strokeWidth="2"
            />
          </g>
        );
      })}
      {links.map((_, i) => {
        const y = 180 + i * 20;
        const x1 = 120 + (i % 2) * 120;
        const x2 = 1080 - (i % 3) * 140;
        return (
          <path
            key={i}
            d={`M ${x1} ${y} C 400 ${y + 40}, 800 ${y - 40}, ${x2} ${y}`}
            fill="none"
            stroke="url(#g1)"
            strokeOpacity=".35"
          />
        );
      })}
    </svg>
  );
}

function NeuralSphereSVG() {
  const rings = 38;
  const dots = Array.from({ length: 42 });
  return (
    <svg
      viewBox="0 0 1000 600"
      xmlns="http://www.w3.org/2000/svg"
      className="svg"
    >
      <defs>
        <radialGradient id="g2" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#ffffff" stopOpacity=".8" />
          <stop offset="100%" stopColor="#22d3ee" stopOpacity=".2" />
        </radialGradient>
      </defs>
      <rect width="100%" height="100%" fill="#0a0a0a" />
      <g transform="translate(500,300)">
        <circle r="180" fill="url(#g2)" opacity=".12" />
        {Array.from({ length: rings }).map((_, i) => {
          const t = i / (rings - 1);
          const r = 220 * Math.sin(Math.acos(1 - 2 * t));
          return (
            <ellipse
              key={i}
              rx={r}
              ry={220 - r * 0.55}
              fill="none"
              stroke="#e879f9"
              strokeOpacity="0.25"
            />
          );
        })}
        {dots.map((_, i) => {
          const a = (i * Math.PI * 2) / dots.length;
          const x = Math.cos(a) * 180;
          const y = Math.sin(a) * 100;
          return <circle key={i} cx={x} cy={y} r="3" fill="#67e8f9" />;
        })}
      </g>
    </svg>
  );
}

function CircuitSVG() {
  const nodes = Array.from({ length: 60 });
  return (
    <svg
      viewBox="0 0 1200 750"
      xmlns="http://www.w3.org/2000/svg"
      className="svg"
    >
      <rect width="100%" height="100%" fill="#0a0a0a" />
      {nodes.map((_, i) => {
        const x = 40 + (i % 12) * 90;
        const y = 40 + Math.floor(i / 12) * 120;
        return (
          <g key={i}>
            <circle cx={x} cy={y} r="3" fill="#6ee7b7" />
            <path
              d={`M${x} ${y} h 50 v 40 h 80`}
              stroke="#a78bfa"
              strokeOpacity=".5"
              fill="none"
            />
          </g>
        );
      })}
      <g opacity=".5">
        <rect x="200" y="560" width="800" height="2" fill="#67e8f9" />
        <rect x="200" y="590" width="800" height="2" fill="#67e8f9" />
      </g>
    </svg>
  );
}

function DemoChartSVG() {
  const pts = [
    [60, 320],
    [180, 260],
    [320, 210],
    [500, 120],
    [620, 160],
    [740, 200],
  ];
  return (
    <svg
      viewBox="0 0 800 450"
      xmlns="http://www.w3.org/2000/svg"
      className="svg"
    >
      <defs>
        <linearGradient id="line" x1="0" x2="1">
          <stop offset="0%" stopColor="#67e8f9" />
          <stop offset="50%" stopColor="#e879f9" />
          <stop offset="100%" stopColor="#6ee7b7" />
        </linearGradient>
      </defs>
      <rect width="100%" height="100%" fill="#0b0b0b" />
      {Array.from({ length: 9 }).map((_, i) => (
        <rect
          key={i}
          x="60"
          y={40 + i * 40}
          width="680"
          height="1"
          fill="#ffffff10"
        />
      ))}
      {Array.from({ length: 10 }).map((_, i) => (
        <rect
          key={i}
          x={60 + i * 68}
          y="40"
          width="1"
          height="360"
          fill="#ffffff10"
        />
      ))}
      <path
        d="M60 320 C 160 260, 260 260, 320 210 S 500 120, 620 160  740 200, 740 200 L 740 400 L 60 400 Z"
        fill="#67e8f933"
      />
      <path
        d="M60 320 C 160 260, 260 260, 320 210 S 500 120, 620 160  740 200, 740 200"
        fill="none"
        stroke="url(#line)"
        strokeWidth="3"
      />
      {pts.map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r="4" fill="#fff" />
      ))}
    </svg>
  );
}

function MapDotsSVG() {
  const dots = Array.from({ length: 800 });
  return (
    <svg
      viewBox="0 0 900 500"
      xmlns="http://www.w3.org/2000/svg"
      className="svg"
    >
      <rect width="100%" height="100%" fill="#0a0a0a" />
      {dots.map((_, i) => {
        const x = (i * 73) % 900;
        const y = (i * 127) % 500;
        const inBand = y > 120 && y < 380;
        return (
          <circle
            key={i}
            cx={x}
            cy={y}
            r={inBand ? 1.2 : 0.8}
            fill={inBand ? "#67e8f9" : "#a78bfa"}
            opacity={inBand ? 0.85 : 0.55}
          />
        );
      })}
      <g>
        <circle cx="430" cy="210" r="5" fill="#6ee7b7" />
        <text x="440" y="210" fill="#e5e7eb" fontSize="12">
          EU
        </text>
      </g>
    </svg>
  );
}

function LogoSVG({ className = "" }) {
  return (
    <svg
      viewBox="0 0 200 200"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <defs>
        <linearGradient id="lg" x1="0" x2="1">
          <stop offset="0%" stopColor="#67e8f9" />
          <stop offset="50%" stopColor="#e879f9" />
          <stop offset="100%" stopColor="#6ee7b7" />
        </linearGradient>
      </defs>
      <rect width="200" height="200" rx="48" fill="#0a0a0a" stroke="url(#lg)" />
      <path
        d="M70 60 h60 a20 20 0 0 1 0 40 h-40 v40"
        fill="none"
        stroke="url(#lg)"
        strokeWidth="12"
        strokeLinecap="round"
      />
      <circle cx="70" cy="140" r="10" fill="#67e8f9" />
    </svg>
  );
}

function IntroVideoOverlay() {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 8000,
        pointerEvents: "none", // damit Banner & Seite weiterhin klickbar sind
        overflow: "hidden",
      }}
    >
      <video
        src="/intro.mp4" // liegt im public-Ordner
        autoPlay
        loop
        muted
        playsInline
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          filter: "brightness(0.6)",
        }}
      />

      {/* dunkler Farbverlauf drüber, damit Text/Content lesbar bleibt */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(circle at top, rgba(76,195,255,0.25), rgba(0,0,0,0.9))",
          mixBlendMode: "soft-light",
        }}
      />
    </div>
  );
}
